import React, { useState, useEffect } from 'react'
import { Box, TextInput, Flex, Group, Button, Select, Textarea } from '@mantine/core'
import { validation } from '../../../../../plugins/validation'
import { useSelector, useDispatch } from 'react-redux'
import { updateOrder } from '../../../../../services/orders'
import { useParams } from 'react-router-dom'
import { getOrderDetail } from '../../../../../store/orderDetail'
import { notificationSuccess, notificationError } from '../../../../ui/Notifications'
import { getAllCountryList, getAllProvinceList, getAllCityList } from '../../../../../services/location'
import { getAllSupplierList } from '../../../../../services/supplier'

const defaultVal = {
  origin: '',
  origin_province: '',
  origin_city: '',
  origin_address: '',
  destination: '',
  supplier: '',
  supplier_name: '',
  shipment_from: null,
  shipment_to: null,
  marking_code: '',
}

const formValidation = {
  origin: {
    isError: false,
    message: ''
  },
  destination: {
    isError: false,
    message: ''
  },
  shipment_from: {
    isError: false,
    message: ''
  },
  shipment_to: {
    isError: false,
    message: ''
  },
}

const FormAdjustAdditionalInfo = ({ onCloseForm }) => {
  const { additionalInfo } = useSelector(state => state.order_detail)
  const dispatch = useDispatch()
  const params = useParams()
  const orderId = params.order_id
  const [formData, setFormData] = useState(defaultVal)
  const [validationForm, setValidationForm] = useState(formValidation)
  const [loadingForm, setLoadingForm] = useState(false)
  const [countryList, setCountryList] = useState([])
  const [provinceList, setProvinceList] = useState([])
  const [cityList, setCityList] = useState([])
  const [supplierList, setSupplierList] = useState([])
  const [searchSupplier, setSearchSupplier] = useState('')

  const submitUpdateAdditionalInfo = async (form) => {
    setLoadingForm(true)
    setValidationForm(formValidation)
    const validateForm = {
      origin: form.origin,
      destination: form.destination,
      shipment_from: form.shipment_from,
      shipment_to: form.shipment_to
    }
    const isError = validation(validateForm, setValidationForm)
    if (isError) {
      setLoadingForm(false)
      return
    }
    const payload = {
      origin: form.origin,
      origin_province: form.origin_province ? Number(form.origin_province) : null,
      origin_city: form.origin_city ? Number(form.origin_city) : null,
      origin_address: form.origin_address,
      destination: form.destination,
      supplier: form.supplier ? Number(form.supplier) : '',
      supplier_name: form.supplier_name,
      shipment_from: form.shipment_from,
      shipment_to: form.shipment_to,
      marking_code: form.marking_code,
    }
    try {
      const response = await updateOrder(orderId, payload)
      if (response.success) {
        onCloseForm()
        dispatch(getOrderDetail(orderId))
        notificationSuccess('Update Order Successfully', 'This data has been updated')
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response.data.message
      notificationError('Update Order Error', `${errorMessage ? errorMessage : 'Please recheck your form'}`)
    } finally {
      setLoadingForm(false)
    }
  }

  const handleGetCountryList = async () => {
    const params = {
      'page[num]': '1',
      'page[limit]': '99999',
    }
    try {
      const response = await getAllCountryList(params)
      const dataCountry = response.data
      const remapDataCountry = dataCountry.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setCountryList(remapDataCountry)
    } catch (error) {
      console.log(error)
    }
  }

  const handleGetProvinceList = async (countryId) => {
    const params = {
      'filter[countries.id][is]': countryId
    }
    try {
      const response = await getAllProvinceList(params)
      const dataProvince = response.data
      const remapDataProvince = dataProvince.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setProvinceList(remapDataProvince)
    } catch (error) {
      console.log(error)
    }
  }

  const handleGetCityList = async (provinceId) => {
    const params = {
      'filter[provinces.id][is]': provinceId
    }
    try {
      const response = await getAllCityList(params)
      const dataCity = response.data
      const remapDataCity = dataCity.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setCityList(remapDataCity)
    } catch (error) {
      console.log(error)
    }
  }

  const handleGetSupplierList = async () => {
    const params = {
      'filter[name][like]': searchSupplier
    }
    try {
      const response = await getAllSupplierList(params)
      const dataSupplier = response.data
      const remapDataSupplier = dataSupplier.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setSupplierList(remapDataSupplier)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    handleGetCountryList()
    if (additionalInfo) {
      handleSetForm(additionalInfo)
    }
    // eslint-disable-next-line
  }, [additionalInfo])

  useEffect(() => {
    handleGetSupplierList()
    // eslint-disable-next-line
  }, [searchSupplier])

  const handleSetForm = (value) => {
    const dataDetail = {
      origin: value.origin,
      origin_province: value.origin_province ? value.origin_province.toString() : null,
      origin_city: value.origin_city ? value.origin_city.toString() : null,
      origin_address: value.origin_address,
      destination: value.destination,
      supplier: value.supplier_id,
      supplier_name: value.supplier_name,
      shipment_from: value.shipment_from,
      shipment_to: value.shipment_to,
      marking_code: value.marking_code,
    }
    if (value.shipment_from !== '') {
      handleGetProvinceList(value.shipment_from)
    }
    if (value.origin_province !== null) {
      handleGetCityList(value.origin_province)
    }
    if (value.supplier_id !== null) {
      setSearchSupplier(value.supplier_name)
    }
    setFormData(dataDetail)
  }

  const handleChangeForm = (name, val) => {
    setFormData((oldVal) => ({ ...oldVal, [name]: val}))
  }

  const handleChangeShipmentFrom = (val) => {
    handleChangeForm('shipment_from', val)
    handleGetProvinceList(val)

    const dataCountry = countryList.find(x => x.value === val)
    handleChangeForm('origin', dataCountry ? dataCountry.label : '')
    if (val === null) {
      handleChangeForm('origin_province', null)
      handleChangeForm('origin_city', null)
    }
  }

  const handleChangeShipmentTo = (val) => {
    handleChangeForm('shipment_to', val)

    const dataCountry = countryList.find(x => x.value === val)
    handleChangeForm('destination', dataCountry ? dataCountry.label : '')
  }

  const handleChangeProvince = (val) => {
    handleChangeForm('origin_province', val)
    handleGetCityList(val)
    handleChangeForm('origin_city', null)
  }

  const handleChangeSupplier = (val) => {
    handleChangeForm('supplier', val)
    const dataSupplier = supplierList.find(x => x.value === val)
    handleChangeForm('supplier_name', dataSupplier ? dataSupplier.label : '')
  }

  const mappingProvinceForm = (countryId) => {
    if (countryId !== null) {
      return (
        <Box mb='md'>
          <Select
            name='origin_province'
            value={formData.origin_province}
            label='Origin Province'
            placeholder='Select Province'
            data={provinceList}
            onChange={(val) => handleChangeProvince(val)}
            size='xs'
            searchable
            nothingFoundMessage="No Province Found"
            clearable
          />
        </Box>
      )
    }
  }

  const mappingCityForm = (provinceId) => {
    if (provinceId !== null) {
      return (
        <Box mb='md'>
          <Select
            name='origin_city'
            value={formData.origin_city}
            label='Origin City'
            placeholder='Select City'
            data={cityList}
            onChange={(val) => handleChangeForm('origin_city', val)}
            size='xs'
            searchable
            nothingFoundMessage="No City Found"
            clearable
          />
        </Box>
      )
    }
  }

  return (
    <Box>
      <Box mb='md'>
        <TextInput
          name='marking_code'
          value={formData.marking_code}
          label='Marking Code'
          placeholder='Input marking code'
          onChange={(val) => handleChangeForm('marking_code', val.target.value)}
          size='xs'
        />
      </Box>
      <Box mb='md'>
        <Select
          name='shipment_from'
          value={formData.shipment_from}
          label='Origin Country'
          placeholder='Select Country'
          error={validationForm.shipment_from.isError ? `${validationForm.shipment_from.message}` : ''}
          data={countryList}
          onChange={(val) => handleChangeShipmentFrom(val)}
          size='xs'
          searchable
          nothingFoundMessage="No Country Found"
          clearable
          withAsterisk
        />
      </Box>
      {mappingProvinceForm(formData.shipment_from)}
      {mappingCityForm(formData.origin_province)}
      <Box mb='md'>
        <Textarea
          name='origin_address'
          label="Origin Address"
          placeholder="Input origin address"
          value={formData.origin_address}
          onChange={(val) => handleChangeForm('origin_address', val.target.value)}
          size='xs'
          autosize
          minRows={4}
          maxRows={4}
        />
      </Box>
      <Box mb='md'>
        <Select
          name='shipment_to'
          value={formData.shipment_to}
          label='Destination'
          placeholder='Select Destination'
          error={validationForm.shipment_to.isError ? `${validationForm.shipment_to.message}` : ''}
          data={countryList}
          onChange={(val) => handleChangeShipmentTo(val)}
          size='xs'
          searchable
          nothingFoundMessage="No Country Found"
          clearable
          withAsterisk
        />
      </Box>
      <Box mb='md'>
        <Select
          name='supplier'
          value={formData.supplier}
          label='Supplier'
          placeholder='Select Supplier'
          data={supplierList}
          onChange={(val) => handleChangeSupplier(val)}
          size='xs'
          searchable
          searchValue={searchSupplier}
          onSearchChange={setSearchSupplier}
          nothingFoundMessage="No Supplier Found"
          clearable
        />
      </Box>
      <Box mt={20}>
        <Flex justify='flex-end'>
          <Group>
            <Button size='xs' variant="outline" onClick={onCloseForm}>Close</Button>
            <Button size='xs' loading={loadingForm} variant="filled" onClick={() => submitUpdateAdditionalInfo(formData)}>{additionalInfo ? 'Update' : 'Create'}</Button>
          </Group>
        </Flex>
      </Box>
    </Box>
  )
}

export default FormAdjustAdditionalInfo